"use client";

import { useState, type ReactNode } from "react";
import { trackCalculatorUsed } from "@/lib/analytics-free-tools";
import NumerologyReportCTA from "@/components/NumerologyReportCTA";
import RatingPrompt from "@/components/RatingPrompt";
import { RESULT_REASONS } from "@/lib/feedback/resultReasons";
import { calculateNameNumber } from "@/lib/api/numerology";
import { Collapsible } from "@ishvaram/ui/Collapsible";
import { ScoreBadge } from "@ishvaram/ui/ScoreBadge";
import { PROFILES } from "./NameNumerology.data";
import { NameForm } from "./NameNumerology.form";
import { NameResult } from "./NameNumerology.result";

export function NameNumerology({
  language = "en",
  sourcePath,
  crossLinks,
}: {
  language?: "en" | "hi";
  sourcePath: string;
  crossLinks: ReactNode;
}) {
  const [name, setName] = useState("");
  const [result, setResult] = useState<{ nameNumber: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleCalculate(e: React.FormEvent) {
    e.preventDefault();
    if (name.trim().length === 0 || loading) return;
    setLoading(true);
    setError(null);
    try {
      // Chaldean name number from the engine — same source of truth as
      // /numerology/, so the two pages never disagree on a name's number.
      const res = await calculateNameNumber(name.trim());
      setResult({ nameNumber: res.name_number.value });
      trackCalculatorUsed({ calculator: "name-numerology" });
    } catch {
      setError("Could not calculate your name number. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setName("");
    setResult(null);
    setError(null);
  }

  const profile = result ? PROFILES.find((p) => p.number === result.nameNumber) : null;

  return (
    <div className="sacred-bg min-h-[calc(100vh-130px)]">
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12 sm:py-16">
        {/* Header */}
        <div className="text-center mb-10">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mb-3 text-ish-accent">
            Chaldean Name Numerology
          </p>
          <h1 className="text-[28px] sm:text-[36px] font-bold leading-tight text-ish-dark font-serif">
            Name Numerology Calculator | नाम अंक ज्योतिष
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed max-w-lg mx-auto text-ish-muted">
            Enter your name to discover your Name Number, personality traits, compatible numbers,
            and career guidance based on the Chaldean (Vedic) numerology system — the same engine as
            our full Numerology Calculator.
          </p>
        </div>

        {!result && <NameForm name={name} onNameChange={setName} onSubmit={handleCalculate} />}

        {loading && <p className="text-center text-[14px] mt-4 text-ish-muted">Calculating…</p>}
        {error && (
          <p className="text-center text-[14px] mt-4 text-warning" role="alert">
            {error}
          </p>
        )}

        {result && profile && (
          <NameResult
            name={name}
            result={result}
            profile={profile}
            resultOffer={
              <NumerologyReportCTA language={language} sourcePath={sourcePath} name={name} />
            }
            onReset={handleReset}
          />
        )}

        {result && profile && (
          <div className="mt-6">
            <RatingPrompt
              source={"tool_result" as never}
              mode="thumbs+reasons"
              reasons={RESULT_REASONS}
              prompt="Was this name numerology accurate?"
              context={{
                surface: "name_numerology_result",
                path: typeof window !== "undefined" ? window.location.pathname : null,
              }}
            />
          </div>
        )}

        {/* All 9 Number Profiles (always visible for SEO) */}
        <Collapsible title="All 9 Name Numbers — Quick Reference" defaultOpen={false}>
          <div className="card-sacred p-6 sm:p-8 mt-0 space-y-4">
            {PROFILES.map((p, idx) => (
              <div
                key={idx}
                className={`flex gap-4 items-start rounded-lg p-4 border ${
                  result?.nameNumber === idx + 1
                    ? "bg-ish-accent/10 border-ish-accent"
                    : "border-ish-border bg-transparent"
                }`}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-[18px] font-bold bg-ish-accent/10 text-ish-accent">
                  {idx + 1}
                </div>
                <div className="min-w-0">
                  <h3 className="text-[15px] font-bold text-ish-dark">
                    {p.title} — {p.planet}
                  </h3>
                  <p className="text-[13px] mt-1 text-ish-muted">
                    {p.characteristics[0]}. {p.characteristics[1]}.
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-[12px] text-ish-faint">Compatibility:</span>
                    <ScoreBadge score={p.compatible.length} max={9} size="sm" />
                    <span className="text-[12px] text-ish-faint ml-2">
                      Lucky Color: {p.lucky_color}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Collapsible>

        {crossLinks}
      </div>
    </div>
  );
}
